import algoliasearch from "algoliasearch/lite";
import { useEnvContext } from "context/EnvContext";
import { DEFAULT_ALGOLIA_QUERY } from "core/consts";
import { AlgoliaAnalyticsName } from "core/types";
import { getIndex } from "dsl/atoms/AlgoliaSearchBar";
import { useMemo } from "react";
import { useGetAnalyticsTags } from "./useGetAnalyticsTags";

export type UseCustomAlgoliaSearchClientProps = {
  algoliaAnalyticsName: AlgoliaAnalyticsName;
  indexName: string;
  indexWithEnv?: boolean;
};

export function useAlgoliaSearchClient({
  algoliaAnalyticsName,
  indexName,
  indexWithEnv,
}: UseCustomAlgoliaSearchClientProps) {
  const { config, env } = useEnvContext();
  const analyticsTags = useGetAnalyticsTags({ algoliaAnalyticsName });
  const indexNameWithEnv = useMemo(
    () => getIndex(indexName, env, indexWithEnv),
    [indexName, env, indexWithEnv],
  );

  const searchClient = useMemo(() => {
    const algoliaClient = algoliasearch(
      config?.algolia.appId ?? "",
      config?.algolia.apiKey ?? "",
    );

    return {
      ...algoliaClient,
      search(queries: Parameters<typeof algoliaClient.search>[0]) {
        if (
          queries.filter(
            ({ params }) => params?.query !== DEFAULT_ALGOLIA_QUERY,
          ).length === 0
        ) {
          return Promise.resolve({ results: [] });
        }
        return algoliaClient.search(queries);
      },
    };
  }, [config?.algolia.appId, config?.algolia.apiKey]);

  return { analyticsTags, indexNameWithEnv, searchClient };
}
